import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  SyncIcon,
  Text,
} from '@billda/design-system';

interface OrgChangedDetails {
  previousOrgId: string;
  newOrgId: string;
  newOrgName?: string;
}

/**
 * Blocking dialog shown when the organisation is changed in another tab.
 *
 * Listens to the 'auth:org-changed-in-other-tab' event dispatched by
 * the multiAccountStore's cross-tab storage sync listener.
 *
 * This dialog is non-dismissable - the user must reload to pick up
 * the new organisation context and avoid stale data.
 */
export function OrgChangedDialog() {
  const [open, setOpen] = useState(false);
  const [orgName, setOrgName] = useState<string | undefined>();

  useEffect(() => {
    const handleOrgChanged = (event: Event) => {
      const customEvent = event as CustomEvent<OrgChangedDetails>;
      setOrgName(customEvent.detail.newOrgName);
      setOpen(true);
    };

    window.addEventListener('auth:org-changed-in-other-tab', handleOrgChanged);

    return () => {
      window.removeEventListener(
        'auth:org-changed-in-other-tab',
        handleOrgChanged
      );
    };
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        // Non-dismissable: swallow escape/outside interactions.
      }}
    >
      <DialogContent
        className="max-w-sm"
        aria-labelledby="org-changed-dialog-title"
        aria-describedby="org-changed-dialog-description"
        onEscapeKeyDown={event => {
          event.preventDefault();
        }}
        onPointerDownOutside={event => {
          event.preventDefault();
        }}
        onInteractOutside={event => {
          event.preventDefault();
        }}
      >
        <DialogHeader
          id="org-changed-dialog-title"
          className="flex items-center gap-3"
        >
          <SyncIcon
            aria-hidden="true"
            className="size-5 text-content-warning"
          />
          Organisation Changed
        </DialogHeader>
        <DialogBody>
          <Text
            as="p"
            variant="body1"
            color="secondary"
            id="org-changed-dialog-description"
          >
            {orgName
              ? `The organisation was switched to "${orgName}" in another tab.`
              : 'The organisation was switched in another tab.'}
          </Text>
          <Text as="p" variant="body2" color="secondary" className="mt-2">
            Please reload this page to continue with the correct organisation
            context.
          </Text>
        </DialogBody>
        <DialogFooter>
          <Button variant="neutral" onClick={handleReload} autoFocus>
            Reload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
